/**
 * The API client.
 *
 * Every backend response is `{ success, data, message }` (or `{ success:false, error }`),
 * so this unwraps the envelope once and everything above it deals in plain data.
 * A 401 triggers one refresh attempt, and concurrent 401s share that single refresh.
 *
 * The Executive Dashboard keeps its own session under its own keys: its token is not an
 * admin token and must never be read as one, nor an admin token as its.
 */

const BASE = '/api/v1';

const ACCESS_KEY = 'viganium.dashboard.accessToken';
const REFRESH_KEY = 'viganium.dashboard.refreshToken';

export interface ApiErrorShape {
  code: string;
  message: string;
  details?: unknown;
}

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details?: unknown;

  constructor(status: number, error: ApiErrorShape) {
    super(error.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = error.code;
    this.details = error.details;
  }
}

export const tokens = {
  get access() {
    return localStorage.getItem(ACCESS_KEY);
  },
  get refresh() {
    return localStorage.getItem(REFRESH_KEY);
  },
  set(access: string, refresh: string) {
    localStorage.setItem(ACCESS_KEY, access);
    localStorage.setItem(REFRESH_KEY, refresh);
  },
  clear() {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

let refreshInFlight: Promise<boolean> | null = null;

async function refreshTokens(): Promise<boolean> {
  const refreshToken = tokens.refresh;
  if (!refreshToken) return false;
  refreshInFlight ??= (async () => {
    try {
      const res = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) return false;
      const body = await res.json();
      tokens.set(body.data.accessToken, body.data.refreshToken);
      return true;
    } catch {
      return false;
    } finally {
      setTimeout(() => (refreshInFlight = null), 0);
    }
  })();
  return refreshInFlight;
}

/** The session is over — access revoked, the account switched off, a refresh refused. */
function signedOut() {
  tokens.clear();
  window.dispatchEvent(new CustomEvent('viganium:signed-out'));
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  params?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
  _retried?: boolean;
}

function urlOf(path: string, params?: RequestOptions['params']): string {
  const url = new URL(`${BASE}${path}`, window.location.origin);
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value !== undefined && value !== null && value !== '') url.searchParams.set(key, String(value));
  }
  return url.toString().replace(window.location.origin, '');
}

export async function request<T = unknown>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, params, signal } = options;
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const access = tokens.access;
  if (access) headers.Authorization = `Bearer ${access}`;

  const res = await fetch(urlOf(path, params), {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  });

  if (res.status === 401 && !options._retried && tokens.refresh) {
    if (await refreshTokens()) return request<T>(path, { ...options, _retried: true });
    signedOut();
    throw new ApiError(401, { code: 'TOKEN_INVALID', message: 'Your session has ended.' });
  }
  if (res.status === 204) return undefined as T;

  const payload = await res.json().catch(() => null);
  if (!res.ok) {
    const error: ApiErrorShape = payload?.error ?? { code: 'NETWORK_ERROR', message: `Request failed (${res.status}).` };
    if (res.status === 401) signedOut();
    throw new ApiError(res.status, error);
  }
  return payload?.data as T;
}

/** POST responses may carry a toast-worthy message alongside the data. */
export async function requestWithMessage<T = unknown>(path: string, options: RequestOptions = {}): Promise<{ data: T; message: string | null }> {
  const { method = 'POST', body, params } = options;
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const access = tokens.access;
  if (access) headers.Authorization = `Bearer ${access}`;
  const res = await fetch(urlOf(path, params), { method, headers, body: body === undefined ? undefined : JSON.stringify(body) });
  if (res.status === 401 && !options._retried && tokens.refresh) {
    if (await refreshTokens()) return requestWithMessage<T>(path, { ...options, _retried: true });
    signedOut();
  }
  const payload = await res.json().catch(() => null);
  if (!res.ok) throw new ApiError(res.status, payload?.error ?? { code: 'NETWORK_ERROR', message: 'Request failed.' });
  return { data: payload?.data as T, message: payload?.message ?? null };
}

/** Downloads an export the API makes (CSV, PDF) with this session's token, as a file. */
export async function download(path: string, params?: RequestOptions['params'], retried = false): Promise<void> {
  const headers: Record<string, string> = {};
  const access = tokens.access;
  if (access) headers.Authorization = `Bearer ${access}`;
  const res = await fetch(urlOf(path, params), { headers });
  if (res.status === 401 && !retried && tokens.refresh) {
    if (await refreshTokens()) return download(path, params, true);
    signedOut();
    throw new ApiError(401, { code: 'TOKEN_INVALID', message: 'Your session has ended.' });
  }
  if (!res.ok) {
    const payload = await res.json().catch(() => null);
    throw new ApiError(res.status, payload?.error ?? { code: 'NETWORK_ERROR', message: `Request failed (${res.status}).` });
  }
  const href = URL.createObjectURL(await res.blob());
  const link = document.createElement('a');
  link.href = href;
  link.download = fileNameFrom(res.headers.get('Content-Disposition')) ?? 'download';
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(href), 10_000);
}

function fileNameFrom(disposition: string | null): string | null {
  if (!disposition) return null;
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1];
  if (encoded) return decodeURIComponent(encoded);
  return /filename="?([^";]+)"?/i.exec(disposition)?.[1] ?? null;
}

export const api = {
  get: <T>(path: string, params?: RequestOptions['params']) => request<T>(path, { params }),
  post: <T>(path: string, body?: unknown, params?: RequestOptions['params']) => request<T>(path, { method: 'POST', body, params }),
};

export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
}

/** `/auth/me` in a dashboard session: who is reading. It carries no permission — the dashboard only reads. */
export interface CurrentUser {
  id: string;
  email: string;
  fullName: string;
  fullNameAr: string | null;
  jobTitle: string | null;
  locale: string;
  app: 'DASHBOARD';
}
